import { useCallback, useRef, useState } from "react";
import type { TemplateVariable } from "../lib/schema-paths.js";

interface TemplateAutocompleteState {
	isOpen: boolean;
	filter: string;
	position: { top: number; left: number };
}

interface UseTemplateAutocompleteResult {
	state: TemplateAutocompleteState;
	handleInputChange: (
		value: string,
		cursorPos: number,
		inputEl: HTMLInputElement | HTMLTextAreaElement,
	) => void;
	handleSelect: (
		path: string,
		currentValue: string,
		cursorPos: number,
	) => { newValue: string; newCursorPos: number };
	dismiss: () => void;
}

export function useTemplateAutocomplete(
	variables: TemplateVariable[] | undefined,
): UseTemplateAutocompleteResult {
	const [state, setState] = useState<TemplateAutocompleteState>({
		isOpen: false,
		filter: "",
		position: { top: 0, left: 0 },
	});
	const triggerPosRef = useRef<number>(-1);

	const handleInputChange = useCallback(
		(value: string, cursorPos: number, inputEl: HTMLInputElement | HTMLTextAreaElement) => {
			if (!variables || variables.length === 0) return;

			const beforeCursor = value.slice(0, cursorPos);
			const triggerIndex = beforeCursor.lastIndexOf("{{");

			if (triggerIndex === -1) {
				setState((s) => (s.isOpen ? { ...s, isOpen: false, filter: "" } : s));
				return;
			}

			const afterTrigger = value.slice(triggerIndex, cursorPos);
			if (afterTrigger.includes("}}")) {
				setState((s) => (s.isOpen ? { ...s, isOpen: false, filter: "" } : s));
				return;
			}

			const filter = beforeCursor.slice(triggerIndex + 2).trim();
			triggerPosRef.current = triggerIndex;

			const rect = inputEl.getBoundingClientRect();
			setState({
				isOpen: true,
				filter,
				position: { top: rect.bottom + 4, left: rect.left },
			});
		},
		[variables],
	);

	const handleSelect = useCallback((path: string, currentValue: string, cursorPos: number) => {
		const triggerIndex = triggerPosRef.current;
		if (triggerIndex === -1) return { newValue: currentValue, newCursorPos: cursorPos };

		const before = currentValue.slice(0, triggerIndex);
		const after = currentValue.slice(cursorPos);
		const insertion = `{{ ${path} }}`;
		const newValue = before + insertion + after;
		const newCursorPos = before.length + insertion.length;

		setState({ isOpen: false, filter: "", position: { top: 0, left: 0 } });
		triggerPosRef.current = -1;

		return { newValue, newCursorPos };
	}, []);

	const dismiss = useCallback(() => {
		setState({ isOpen: false, filter: "", position: { top: 0, left: 0 } });
		triggerPosRef.current = -1;
	}, []);

	return { state, handleInputChange, handleSelect, dismiss };
}
